// frontend/src/pages/InvoicesArchive.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || "";

const lastMonths = () => {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    out.push({
      value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`,
      label: d.toLocaleDateString("it-IT", { year: "numeric", month: "long" }),
    });
  }
  return out;
};


const formatDateIT = (value) => {
  if (!value) return "";
  const m = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[3]}/${m[2]}/${m[1]}`;
  return String(value);
};

const isOverdue = (row) => {
  const paid = row?.is_paid || String(row?.status || "").toUpperCase() === "PAID";
  if (paid || !row?.date) return false;
  const due = new Date(`${String(row.date).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(due.getTime())) return false;
  due.setDate(due.getDate() + 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

const overdueDays = (row) => {
  if (!isOverdue(row)) return 0;
  const due = new Date(`${String(row.date).slice(0, 10)}T00:00:00`);
  due.setDate(due.getDate() + 10);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(0, Math.floor((today - due) / 86400000));
};


const MONTH_OPTIONS = [
  { value: "01", label: "Gennaio" },
  { value: "02", label: "Febbraio" },
  { value: "03", label: "Marzo" },
  { value: "04", label: "Aprile" },
  { value: "05", label: "Maggio" },
  { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" },
  { value: "08", label: "Agosto" },
  { value: "09", label: "Settembre" },
  { value: "10", label: "Ottobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Dicembre" },
];

const yearOptions = () => {
  const y = new Date().getFullYear();
  const out = [];
  for (let i = y + 1; i >= y - 8; i--) out.push(String(i));
  return out;
};

const fmtMoney = (v) =>
  Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const statoIT = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "DRAFT") return "Bozza";
  if (s === "SENT") return "Inviata";
  if (s === "PAID") return "Pagata";
  if (s === "CANCELED" || s === "CANCELLED") return "Annullata";
  return status || "—";
};

const authHeaders = () => {
  const token = localStorage.getItem("access") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function InvoicesArchive() {
  const nav = useNavigate();
  const location = useLocation();
  const tipoParam = new URLSearchParams(location.search).get("tipo");
  const [tab, setTab] = useState(tipoParam === "ricevute" ? "ricevute" : "emesse"); // ricevute | emesse
  const [role, setRole] = useState("");
  const [month, setMonth] = useState(lastMonths()[0].value);
  const [year, setYear] = useState(() => lastMonths()[0].value.slice(0, 4));
  const [monthNum, setMonthNum] = useState(() => lastMonths()[0].value.slice(5, 7));
  const [status, setStatus] = useState("");
  const [paid, setPaid] = useState("");
  const [q, setQ] = useState("");

  const [rows, setRows] = useState([]);
  const [globalUnpaid, setGlobalUnpaid] = useState({ count: 0, total: 0 });
  const [loading, setLoading] = useState(false);

  const canManageIncassi = ["MADRE", "SEDE", "FRANCHISING", "PV"].includes(role);
  const isClienteArchive = !canManageIncassi;

  useEffect(() => {
    fetch(`${API_URL}/api/me/`, { headers: { ...authHeaders() } })
      .then((r) => r.ok ? r.json() : null)
      .then((me) => setRole(String(me?.role || "").toUpperCase()))
      .catch(() => setRole(""));
  }, []);

  const fetchGlobalUnpaid = async () => {
    if (!canManageIncassi || tab !== "emesse") {
      setGlobalUnpaid({ count: 0, total: 0 });
      return;
    }
    try {
      const url = new URL(`${API_URL}/api/fatture/`);
      url.searchParams.set("paid", "0");
      if (!["MADRE", "SEDE"].includes(role)) url.searchParams.set("mine", "1");
      url.searchParams.set("page_size", "100");
      const r = await fetch(url.toString(), { headers: { ...authHeaders() } });
      const data = r.ok ? await r.json() : [];
      const items = Array.isArray(data) ? data : (data.results || data.items || []);
      const count = typeof data?.count === "number" ? data.count : items.length;
      const total = items.reduce((acc, x) => acc + Number(x.total || 0), 0);
      setGlobalUnpaid({ count, total });
    } catch {
      setGlobalUnpaid({ count: 0, total: 0 });
    }
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      if (tab === "ricevute") {
        const url = new URL(`${API_URL}/api/fatture/inbox/`);
        url.searchParams.set("month", month);
        if (status) url.searchParams.set("status", status);
        if (paid) url.searchParams.set("paid", paid);
        if (q) url.searchParams.set("q", q);
        const r = await fetch(url.toString(), { headers: { ...authHeaders() } });
        const data = r.ok ? await r.json() : [];
        setRows(Array.isArray(data) ? data : (data.results || data.items || []));
        } else {
          // emesse (le mie o tutte se MADRE)
          const url = new URL(`${API_URL}/api/fatture/`);
            if (paid !== "0") {
              url.searchParams.set("month", month);
            }
          if (status) url.searchParams.set("status", status);
            if (paid) url.searchParams.set("paid", paid);
          if (q) url.searchParams.set("q", q);
            if (!["MADRE", "SEDE"].includes(role)) url.searchParams.set("mine", "1");
          url.searchParams.set("page_size", "100");
          const r = await fetch(url.toString(), { headers: { ...authHeaders() } });
          const data = r.ok ? await r.json() : [];
          const items = Array.isArray(data) ? data : (data.results || data.items || []);
            const sortedItems = paid === "0"
              ? [...items].sort((a, b) => {
                  const da = String(a.date || "9999-12-31");
                  const db = String(b.date || "9999-12-31");
                  if (da !== db) return da.localeCompare(db);
                  return Number(b.total || 0) - Number(a.total || 0);
                })
              : items;
            setRows(sortedItems);
        }
    } catch {
      setRows([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, month, status, paid, q, role]);

  useEffect(() => {
    fetchGlobalUnpaid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, canManageIncassi, role]);

  const kpi = useMemo(() => {
    const list = rows || [];
    const total = list.reduce((acc, r) => acc + Number(r.total || 0), 0);
    const count = list.length;
    const paidRows = list.filter((r) => r.is_paid || String(r.status || "").toUpperCase() === "PAID");
    const paidCount = paidRows.length;
    const unpaidCount = count - paidCount;
    const paidTotal = paidRows.reduce((acc, r) => acc + Number(r.total || 0), 0);
    const unpaidTotal = Math.max(total - paidTotal, 0);
    const paidPerc = total > 0 ? Math.round((paidTotal / total) * 100) : 0;
    return { total, count, paidCount, unpaidCount, paidTotal, unpaidTotal, paidPerc };
  }, [rows]);

  const openPdf = async (id) => {
    try {
      const r = await fetch(`${API_URL}/api/fatture/${id}/pdf/`, {
        headers: { ...authHeaders() },
      });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("Impossibile aprire il PDF.");
    }
  };

  return (
    <div className="container mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">🗃️ Archivio proforme</h2>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-secondary" onClick={() => nav("/fatture/archivio-menu")}>
              ← Torna all'archivio proforme
            </button>
            <button className="btn btn-outline-secondary" onClick={() => nav("/")}>
              ↩︎ Menu principale
            </button>
          </div>
      </div>

      <div className="d-flex gap-2 mb-3">
        <button
          className={`btn btn-sm ${tab === "ricevute" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setTab("ricevute")}
        >
          Ricevute
        </button>
        <button
          className={`btn btn-sm ${tab === "emesse" ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => setTab("emesse")}
        >
          Emesse
        </button>
      </div>

          <div className="row g-3 mb-3">
            <div className="col-6 col-md-2">
              <div className={`card shadow-sm ${paid===""?"border border-3 border-primary bg-light":""}`} role="button" title="Mostra tutte" onClick={() => { setPaid(""); setStatus(""); }}>
                <div className="card-body">
                  <div className="small text-muted">Totale mese</div>
                  <div className="h4 mb-0">€ {fmtMoney(kpi.total)}</div>
                </div>
              </div>
            </div>

            {canManageIncassi && tab === "emesse" && (
              <>
                <div className="col-6 col-md-2">
                  <div className={`card shadow-sm ${(paid==="1" || status==="PAID")?"border border-3 border-success bg-light":""}`} role="button" title="Mostra pagate" onClick={() => { setPaid("1"); setStatus("PAID"); }}>
                    <div className="card-body">
                      <div className="small text-muted">Incassato</div>
                      <div className="h4 mb-0">€ {fmtMoney(kpi.paidTotal)}</div>
                    </div>
                  </div>
                </div>

                <div className="col-6 col-md-2">
                  <div className={`card shadow-sm ${paid==="0"?"border border-3 border-danger bg-light":""}`} role="button" title="Mostra non pagate" onClick={() => { setPaid("0"); setStatus(""); setQ(""); setMonth(""); }}>
                    <div className="card-body">
                      <div className="small text-muted">Da incassare (totale)</div>
                      <div className="h4 mb-0">€ {fmtMoney(globalUnpaid.total)}</div>
                    </div>
                  </div>
                </div>

                <div className="col-6 col-md-2">
                  <div className="card shadow-sm">
                    <div className="card-body">
                      <div className="small text-muted">% incasso</div>
                      <div className="h4 mb-0">{kpi.paidPerc}%</div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {isClienteArchive && (
              <div className="col-6 col-md-2">
                <div className={`card shadow-sm ${paid==="0"?"border border-3 border-warning bg-light":""}`} role="button" title="Mostra da pagare" onClick={() => { setPaid("0"); setStatus(""); }}>
                  <div className="card-body">
                    <div className="small text-muted">Da pagare</div>
                    <div className="h4 mb-0">€ {fmtMoney(kpi.unpaidTotal)}</div>
                  </div>
                </div>
              </div>
            )}

            <div className="col-6 col-md-2">
              <div className={`card shadow-sm ${(paid==="1" || status==="PAID")?"border border-3 border-success bg-light":""}`} role="button" title="Mostra pagate" onClick={() => { setPaid("1"); setStatus("PAID"); }}>
                <div className="card-body">
                  <div className="small text-muted">Pagate</div>
                  <div className="h4 mb-0">{kpi.paidCount}</div>
                </div>
              </div>
            </div>

            <div className="col-6 col-md-2">
              <div className={`card shadow-sm ${paid==="0"?"border border-3 border-danger bg-light":""}`} role="button" title={isClienteArchive ? "Mostra da pagare" : "Mostra non pagate"} onClick={() => { setPaid("0"); setStatus(""); }}>
                <div className="card-body">
                  <div className="small text-muted">{isClienteArchive ? "Da pagare (mese)" : "Non pagate (mese)"}</div>
                  <div className="h4 mb-0">{kpi.unpaidCount}</div>
                </div>
              </div>
            </div>
          </div>

        {canManageIncassi && tab === "emesse" && globalUnpaid.count > 0 && (
          <div className="alert border border-warning bg-warning-subtle text-dark d-flex justify-content-between align-items-center mb-3 shadow-sm">
            <div className="d-flex align-items-center gap-3">
              <div style={{ fontSize: 26 }}>⚠️</div>
              <div>
                <div className="fw-bold">Incassi da controllare</div>
                <div className="small">
                  <strong>{globalUnpaid.count}</strong> proforme ancora da incassare
                  · Totale <strong>€ {fmtMoney(globalUnpaid.total)}</strong>
                </div>
              </div>
            </div>
            <button className="btn btn-sm btn-outline-dark" onClick={() => { setPaid("0"); setStatus(""); }}>
              Mostra tutte da incassare
            </button>
          </div>
        )}

      <div className="card shadow-sm">
        <div className="card-header d-flex flex-wrap gap-2 align-items-center">
          <div className="d-flex align-items-center gap-2">
            <span>Mese:</span>
            <select
              className="form-select form-select-sm"
              style={{ width: 130 }}
              value={monthNum}
              onChange={(e) => {
                const m = e.target.value;
                setMonthNum(m);
                setMonth(`${year}-${m}`);
              }}
            >
              {MONTH_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
            <select
              className="form-select form-select-sm"
              style={{ width: 100 }}
              value={year}
              onChange={(e) => {
                const y = e.target.value;
                setYear(y);
                setMonth(`${y}-${monthNum}`);
              }}
            >
              {yearOptions().map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <input
            className="form-control form-control-sm"
            style={{ width: 260 }}
            placeholder="Cerca ragione sociale / numero / email"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchRows()}
          />

          <select
            className="form-select form-select-sm"
            style={{ width: 160 }}
            value={status}
            onChange={(e) => { const v = e.target.value; setStatus(v); if (v==="PAID") setPaid("1"); if (v==="") setPaid(""); }}
          >
            <option value="">Stato (tutti)</option>
            <option value="DRAFT">Bozza</option>
            <option value="ISSUED">Emessa</option>
            <option value="SENT">Inviata</option>
            <option value="PAID">Pagata</option>
          </select>

          <select
            className="form-select form-select-sm"
            style={{ width: 170 }}
            value={paid}
            onChange={(e) => { const v = e.target.value; setPaid(v); if (v==="0") setStatus(""); if (v==="1") setStatus("PAID"); }}
            disabled={tab !== "ricevute"}
          >
            <option value="">Incasso (tutti)</option>
            <option value="1">Pagate</option>
            <option value="0">Da incassare (totale)</option>
          </select>

          <div className="ms-auto d-flex gap-2">
            <button className="btn btn-sm btn-primary" onClick={fetchRows}>
              Applica
            </button>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => {
                setStatus("");
                setPaid("");
                setQ("");
                fetchRows();
              }}
            >
              Reset
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table table-sm mb-0 align-middle">
            <thead>
              <tr>
                <th>#</th>
                <th>Serie/N.</th>
                <th>Data</th>
                <th>Cliente</th>
                <th className="text-end">Totale</th>
                <th>Stato</th>
                <th className="text-end">PDF</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Caricamento…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted">
                    Nessun risultato.
                  </td>
                </tr>
              ) : (
                rows.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{`${r.series || ""}/${r.number || ""}`}</td>
                      <td>{formatDateIT(r.date)}</td>
                      <td>{r.fatt_ragione_sociale || "—"}</td>
                      <td className="text-end">€ {fmtMoney(r.total)}</td>
                      <td>
                        {r.is_paid ? (
                          <span className="badge bg-success">PAGATA</span>
                        ) : (
                          <div className="d-flex align-items-center gap-1">
                            <span className="badge bg-secondary">{statoIT(r.status)}</span>
                            {isOverdue(r) && (
                              <span className="badge bg-danger">In ritardo {overdueDays(r)}g</span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => nav(`/fatture/${r.id}`)}>
                            Apri
                          </button>
                          <button className="btn btn-sm btn-outline-primary" onClick={() => openPdf(r.id)}>
                            PDF
                          </button>
                        </div>
                      </td>
                    </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
